import { init, setUser } from "@sentry/browser";
import { createBeforeSend, type ScrubOptions } from "./scrub";

/**
 * Initialise browser-side Sentry with PII scrubbing applied by default.
 *
 * The browser SDK records fetch/xhr and console breadcrumbs, so it leaks the
 * same member data the server does — the scrub is not server-only.
 */
export function initSentry(dsn: string, options?: ScrubOptions): void {
  if (!dsn) {
    return;
  }
  init({
    dsn,
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
    beforeSend: createBeforeSend({ extraCookies: options?.extraCookies }),

    // Pinned rather than relied upon as a default: `sendDefaultPii` would attach
    // IP address and request headers to browser events. See GH #1085.
    //
    // No contextLines control here, deliberately. The browser SDK does not read
    // source files — for bundled JS, context lines are added by the ingestion
    // backend during symbolication, so the source-exposure question is answered
    // by whether we upload source maps, not by an init option. The v9
    // `contextLinesIntegration` covers only JS inlined into the HTML document,
    // which none of these apps ship, and is not enabled by default anyway.
    // `includeLocalVariables` is likewise server-only.
    sendDefaultPii: false,
  });
}

/**
 * Associate the current authenticated user with subsequent error events so
 * Better Stack shows affected-user counts instead of "0 affected users".
 *
 * Pass `null` on sign-out to clear the association.
 *
 * Only the account `id` is attached — deliberately not email. Better Stack is
 * on the free tier with no binding DPA (`docs/popi/dpa/status.md`), and sending
 * personal data to a processor with no DPA in place is the POPIA exposure we
 * can actually remove; an opaque UUID is not personal data to Better Stack.
 * Affected-user counts still work, and the id resolves against
 * `shared.accounts` when a real incident needs a name. See GH #1085.
 *
 * If a paid tier with an executed DPA lands later, re-adding email is a
 * deliberate decision to reopen that exposure — not a default to drift back to.
 */
export function identifyUser(user: { id: string } | null): void {
  if (user) {
    setUser({ id: user.id });
    return;
  }
  setUser(null);
}

export {
  addBreadcrumb,
  captureException,
  captureMessage,
  setUser,
  withScope,
} from "@sentry/browser";
